<template>
	<div class="range">
		<div class="range__top top">
			<p class="top__title">{{ $t('Price') }}</p>
			<div class="top__info">
				<div class="top__item">
					<div class="top__label">{{ $t('from') }}</div>
					<div class="top__value">
						<span class="top__number">{{ minValue }}</span>
						<img
							class="top__icon"
							src="@/assets/images/coin.svg"
							alt=""
						/>
					</div>
				</div>
				<div class="top__item">
					<div class="top__label">{{ $t('to') }}</div>
					<div class="top__value">
						<span class="top__number">{{ formatNumber(maxBarValue) }}</span>
						<img
							class="top__icon"
							src="@/assets/images/coin.svg"
							alt=""
						/>
					</div>
				</div>
			</div>
		</div>
		<div class="range__track track">
			<div class="track__inner">
				<div class="track__range">
					<div ref="activeRange" class="track__active-range"></div>
				</div>
				<input
					ref="minHandle"
					class="track__input track__input_min"
					type="range"
					:min="0"
					:max="maxBarValue"
					step="1"
					v-model.number="rangeMinLocal"
					@input="onMinSlide"
					@change="commitMinFromSlider"
					aria-label="Minimum price"
				/>
				<input
					ref="maxHandle"
					class="track__input track__input_max"
					type="range"
					:min="0"
					:max="maxBarValue"
					step="1"
					v-model.number="rangeMaxLocal"
					@input="onMaxSlide"
					@change="commitMaxFromSlider"
					aria-label="Maximum price"
				/>
			</div>
			<div class="track__labels">
				<span class="track__label">
					{{ formatNumber(minValueInput) }}
					<img
						class="track__icon"
						src="@/assets/images/coin.svg"
						alt=""
					/>
				</span>
				<span class="track__label">
					{{ formatNumber(maxValueInput) }}
					<img
						class="track__icon"
						src="@/assets/images/coin.svg"
						alt=""
					/>
				</span>
			</div>
		</div>
		<div class="range__actions actions">
			<input
				v-model="minValueInput"
				type="text"
				@input="filterNumericInput($event, 'minValueInput')"
				@blur="handleMinBlur"
				pattern="[0-9.]*"
				inputmode="decimal"
			/>
			<input
				v-model="maxValueInput"
				type="text"
				@input="filterNumericInput($event, 'maxValueInput')"
				@blur="handleMaxBlur"
				pattern="[0-9.]*"
				inputmode="decimal"
			/>
		</div>
	</div>
</template>

<script>
export default {
	props: {
		minValue: { type: Number, required: true },
		maxValue: { type: [Number, null], required: true },
		maxBarValue: { type: Number, required: true },
	},
	emits: ['update-range'],
	data() {
		return {
			minValueInput: String(this.minValue),
			maxValueInput: this.maxValue === null ? '' : String(this.maxValue),
			rangeMinLocal: this.minValue,
			rangeMaxLocal:
				this.maxValue === null ? this.maxBarValue : this.maxValue,
		};
	},
	mounted() {
		this.rangeMinLocal = this.minValue;
		this.rangeMaxLocal =
			this.maxValue === null ? this.maxBarValue : this.maxValue;

		this.$nextTick(() => {
			this.updateActiveRange(this.minValue, this.maxValue);
		});
	},
	watch: {
		minValue(newValue) {
			this.minValueInput = String(newValue);
			this.rangeMinLocal = newValue;
			this.updateActiveRange(newValue, this.maxValue);
		},
		maxValue(newValue) {
			this.maxValueInput = newValue === null ? '' : String(newValue);
			this.rangeMaxLocal = newValue === null ? this.maxBarValue : newValue;
			this.updateActiveRange(this.minValue, newValue);
		},
		maxBarValue() {
			this.rangeMinLocal = this.minValue;
			this.rangeMaxLocal =
				this.maxValue === null ? this.maxBarValue : this.maxValue;
			this.updateActiveRange(this.minValue, this.maxValue);
		},
	},
	methods: {
		handleMinBlur() {
			//this.maxValueInput.trim() === ''
			if (String(this.minValueInput).trim() === '') {
				this.updateActiveRange(this.minValue, null);
				this.$emit('update-range', { min: this.minValue, max: null });
				return;
			}
			let parsedMin = this.safeParseNumber(
				this.minValueInput,
				0,
				this.maxBarValue
			);
			const currentMax =
				this.maxValue === null ? this.maxBarValue : this.maxValue;

			if (parsedMin > currentMax) {
				parsedMin = currentMax;
			}

			//this.minValueInput = parsedMin;
			this.minValueInput = String(parsedMin);

			this.updateActiveRange(
				parsedMin,
				this.maxValue === null ? this.maxBarValue : this.maxValue
			);

			this.$emit('update-range', {
				min: parsedMin,
				max: this.maxValue,
			});
		},
		handleMaxBlur() {
			//this.maxValueInput.trim() === ''
			if (String(this.maxValueInput).trim() === '') {
				this.$emit('update-range', {
					min: this.minValue,
					max: null,
				});
				return;
			}

			let parsedMax = this.safeParseNumber(
				this.maxValueInput,
				0,
				this.maxBarValue
			);

			if (parsedMax < this.minValue) {
				parsedMax = this.minValue;
			}

			//this.maxValueInput = parsedMax;
			this.maxValueInput = String(parsedMax);

			this.updateActiveRange(this.minValue, parsedMax);

			this.$emit('update-range', {
				min: this.minValue,
				max: parsedMax,
			});
		},
		safeParseNumber(value, min, max) {
			if (value === null || String(value).trim() === '') return min;
			const num = parseFloat(String(value).replace(/[^0-9.]/g, ''));
			if (isNaN(num)) return min;
			return Math.max(min, Math.min(max, num));
		},
		filterNumericInput(event, field) {
			let value = event.target.value;
			const parts = value.split('.');
			if (parts.length > 2) {
				value = this[field]; // Revert on second dot
			} else if (parts.length === 2 && parts[1].length > 2) {
				value = this[field]; // Revert on third decimal
			} else if (/[^0-9.]/.test(value)) {
				value = this[field]; // Revert on non-numeric
			}
			this[field] = value;
		},
		onMinSlide(e) {
			const raw = Number(e.target.value) || 0;
			const maxGuard =
				this.maxValue === null ? this.rangeMaxLocal : this.maxValue;
			const clamped = Math.max(
				0,
				Math.min(this.maxBarValue, Math.min(raw, maxGuard))
			);

			this.rangeMinLocal = clamped;
			//this.minValueInput = clamped;
			this.minValueInput = String(clamped);

			// visual update as you drag
			const visualMax =
				this.maxValue === null ? this.rangeMaxLocal : this.maxValue;
			this.updateActiveRange(clamped, visualMax);
			console.log('e', e);
			console.log('clamped', clamped);
			console.log('visualMax', visualMax);
		},
		commitMinFromSlider() {
			const min = this.rangeMinLocal;
			this.$emit('update-range', { min, max: this.maxValue });
		},
		onMaxSlide(e) {
			const raw = Number(e.target.value) || 0;
			const clamped = Math.max(
				this.rangeMinLocal,
				Math.min(this.maxBarValue, raw)
			);

			this.rangeMaxLocal = clamped;
			//this.maxValueInput = clamped;
			this.maxValueInput = String(clamped);

			// visual update as you drag
			const visualMax =
				this.maxValue === null ? this.rangeMaxLocal : clamped;
			this.updateActiveRange(this.rangeMinLocal, visualMax);
		},
		commitMaxFromSlider() {
			const max = this.rangeMaxLocal;
			// if user drags to the end and your semantics want "null" = open-ended, keep as number here
			this.$emit('update-range', { min: this.minValue, max });
		},
		// updateActiveRange(min, max) {
		//	const el = this.$refs.activeRange;
		//	if (!el) return;

		//	const maxBar = this.maxBarValue || 1;

		//	// If max is null, treat it as the full bar for visuals
		//	const visualMax = (max === null || max === undefined) ? maxBar : max;

		//	// Clamp & guard
		//	const safeMin = Math.max(0, Math.min(maxBar, Number(min) || 0));
		//	const safeMax = Math.max(safeMin, Math.min(maxBar, Number(visualMax)));

		//	const leftPct  = (safeMin / maxBar) * 100;
		//	const rightPct = 100 - (safeMax / maxBar) * 100;

		//	el.style.left  = `${leftPct}%`;
		//	el.style.right = `${rightPct}%`;
		//},
		updateActiveRange(min, max) {
			const bar = this.$refs.activeRange;
			if (!bar) return;

			const maxBar = this.maxBarValue || 1;
			const visualMax =
				max === null || max === undefined ? maxBar : Number(max);

			const safeMin = Math.max(0, Math.min(maxBar, Number(min) || 0));
			const safeMax = Math.max(safeMin, Math.min(maxBar, visualMax));

			const leftPct = (safeMin / maxBar) * 100;
			const rightPct = 100 - (safeMax / maxBar) * 100;

			// Active filled bar
			bar.style.left = `${leftPct}%`;
			bar.style.right = `${rightPct}%`;
			this.rangeMinLocal = safeMin;
			this.rangeMaxLocal = this.maxValue === null ? maxBar : safeMax;
		},
		formatNumber(value) {
			const num = Number(value);
			if (Number.isNaN(num)) return '0';
			// Format with spaces as thousand separators, no decimals if .00
			const formatter = new Intl.NumberFormat('fr-FR', {
				minimumFractionDigits: 0,
				maximumFractionDigits: 2,
			});
			return formatter.format(num);
		},
	},
};
</script>

<style lang="scss" scoped>
@use '@/assets/styles/mixins' as *;
@use '@/assets/styles/media' as *;
@use '@/assets/styles/components/classes' as *;

.range {
	&:not(:last-child) {
		@include adaptiveValue('margin-bottom', 35, 15);
	}
	&__top {
		&:not(:last-child) {
			@include adaptiveValue('margin-bottom', 15, 10);
		}
	}
	&__track {
	}
	&__actions {
	}
}

.top {
	display: flex;
	gap: 20px;
	justify-content: space-between;
	&__title {
	}
	&__item,
	&__info {
		display: flex;
		align-items: center;
		gap: 4px;
	}
	&__label {
		font-weight: 400;
		color: var(--secondary-color);
	}
	&__value {
		display: flex;
		align-items: center;
		gap: 4px;
		line-height: 1;
	}
	&__number {
		line-height: 1;
	}
	&__icon {
		width: 16px;
		height: 16px;
		flex: 0 0 16px;
	}
}

.track {
	&:not(:last-child) {
		margin: 0px 0px 20px 0px;
	}
	&__inner {
		position: relative;
		width: 100%;
		height: 15px;
		&:not(:last-child) {
			margin: 0px 0px 10px 0px;
		}
	}
	&__active-range,
	&__range {
		position: absolute;
		top: 50%;
		height: 4px;
		transform: translate(0px, -50%);
	}
	&__range {
		width: 100%;
		left: 0px;
		right: 0px;
		background-color: var(--secondary-color);
	}
	&__active-range {
		left: 0;
		right: 0;
		height: 6px;
		background-color: var(--hint-color);
	}
	&__input {
		pointer-events: none;
		position: absolute;
		width: 100%;
		left: 0;
		right: 0;
		height: 15px;
		background: none;
		appearance: none;
		-webkit-appearance: none;
		-moz-appearance: none;
		z-index: 3;
		&::-webkit-slider-thumb {
			pointer-events: all;
			-webkit-appearance: none;
			height: 15px;
			width: 15px;
			border-radius: 50%;
			background: var(--hint-color);
			cursor: pointer;
			transition: background-color 0.3s ease 0s;
		}
		&::-moz-range-thumb {
			pointer-events: all;
			height: 15px;
			width: 15px;
			border-radius: 50%;
			background: var(--hint-hover-color);
			cursor: pointer;
			transition: background-color 0.3s ease 0s;
		}
		&:focus::-webkit-slider-thumb {
			background: var(--hint-hover-color);
		}
		&:focus::-moz-range-thumb {
			background: var(--hint-hover-color);
		}
		&_min {
		}
		&_max {
			right: 0px;
			z-index: 3;
		}
	}
	&__labels {
		display: flex;
		gap: 20px;
		justify-content: space-between;
	}
	&__label {
		display: flex;
		align-items: center;
		gap: 4px;
		line-height: 1;
	}
	&__icon {
		width: 14px;
		height: 14px;
		flex: 0 0 14px;
	}
}

.actions {
	display: flex;
	@include adaptiveValue('gap', 21, 10);
	input {
		width: 50%;
		padding: 0px 15px;
		display: flex;
		align-items: center;
		justify-content: center;
		@include adaptiveValue('height', 36, 44);
		background-color: var(--bg-secondary-color);
		border: 1px solid var(--primary-color);
		border-radius: var(--border-radius-primary);
		font-size: var(--font-size-base);
		color: var(--primary-color);
		font-weight: 700;
		outline: transparent;
		transition: border 0.3s ease 0s;
		&:focus {
			border: 1px solid var(--hint-color);
		}
	}
}
</style>
